import { Flex, Table, TableProps } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

// components
import NoData from 'components/noData/noData';
import ActionListHeaderComponent from 'modules/main/actions/listHeader';
import ListButton from 'components/listButton/listButton';
import StatusComponent from 'components/status/status';

// services

// styles

// models
import { Assembly } from '../models/assemblies';
import NotificationService from 'core/services/notification';
import { onCancelAssembly, onGetAssembliesList, onStartAttendance, onStartVoting } from '../services/assembliesServices';
import TableTotal from 'components/tableTotal/tableTotal';
import { StatusColors } from 'utils/statusColors';
import { isRTL } from 'core/services/language';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import VerificationModal from '../../verificationModal';
import { ServiceCost, VerificationPayload } from '../../commonModels';
import { onGetServiceCost } from '../../commonServices';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

interface ActionAssembliesListPageProps {}

const ActionAssembliesListPage: FunctionComponent<ActionAssembliesListPageProps> = () => {
	const { t } = useTranslation();

	const { suspendedProps } = useCompanySuspended();

	const navigate = useNavigate();
	const location = useLocation();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [requests, setRequests] = useState<Partial<Assembly>[]>([]);

	const [serviceCostLoading, setServiceCostLoading] = useState<boolean>(true);
	const [serviceCost, setServiceCost] = useState<ServiceCost>();

	const [isCanceling, setIsCanceling] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [selectedRequestId, setSelectedRequestId] = useState<number>();
	const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);

	const [votingVerificationModalOpen, setVotingVerificationModalOpen] = useState<boolean>(false);
	const [attendanceVerificationModalOpen, setAttendanceVerificationModalOpen] = useState<boolean>(false);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const columns: TableProps<Partial<Assembly>>['columns'] = [
		{
			title: t('ACTIONS.REQUEST_CODE'),
			dataIndex: 'request_number',
			key: 'request_number',
			render: (request_number) => `#${request_number}`,
		},
		{
			title: t('ACTIONS.REQUEST_DATE'),
			dataIndex: 'created_at',
			key: 'created_at',
		},
		{
			title: t('ACTIONS.LOCATION'),
			dataIndex: 'location_type',
			key: 'location_type',
			render: (location_type) =>
				location_type === 'Both' ? (
					<Flex gap={10} justify="center">
						<StatusComponent small text={t(`ACTIONS.PHYSICAL`)} color={'green'} />
						<StatusComponent small text={t(`ACTIONS.ONLINE`)} color={'green'} />
					</Flex>
				) : (
					<StatusComponent small text={t(`ACTIONS.${location_type.toUpperCase()}`)} color={'green'} />
				),
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <StatusComponent text={t(`COMMON.${status.toUpperCase()}`)} color={StatusColors[status]} />,
		},
		{
			title: t('ACTIONS.ASSEMBLY_DATE'),
			dataIndex: 'date',
			key: 'date',
		},
		{
			title: t('ACTIONS.ASSEMBLY_TYPE'),
			dataIndex: 'translated_type',
			key: 'translated_type',
			render: (translated_type) => (isRTL() ? translated_type.ar : translated_type.en),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) => (
				<Flex gap={10} justify="center">
					<ListButton type="view" onClick={() => navigate(`view/${record.id}`)} text={t('COMMON.VIEW')} />
					{record.can_edit_or_cancel && (
						<>
							<ListButton type="edit" {...suspendedProps} onClick={() => navigate(`${record.id}`)} text={t('COMMON.EDIT')} />
							<ListButton
								type="reject"
								{...suspendedProps}
								onClick={() => {
									setSelectedRequestId(record.id);
									setCancelModalOpen(true);
								}}
								text={t('COMMON.CANCEL')}
							/>
						</>
					)}
					{record.can_open_assembly_attendance && (
						<ListButton
							type="accept"
							{...suspendedProps}
							onClick={() => {
								setSelectedRequestId(record.id);
								setAttendanceVerificationModalOpen(true);
							}}
							text={t('ACTIONS.OPEN_ATTENDANCE')}
						/>
					)}
					{record.can_open_voting && (
						<ListButton
							type="vote"
							{...suspendedProps}
							onClick={() => {
								setSelectedRequestId(record.id);
								setVotingVerificationModalOpen(true);
							}}
							text={t('ACTIONS.OPEN_VOTING')}
						/>
					)}
				</Flex>
			),
		},
	];

	useEffect(() => {
		setCurrentPage(getCurrentPage());
		getAssemblies();
		getServiceCost();
	}, []);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getServiceCost = async () => {
		try {
			setServiceCostLoading(true);

			const response = await onGetServiceCost('assemblies');
			setServiceCost(response?.success);

			setServiceCostLoading(false);
		} catch (error) {
			setServiceCostLoading(false);
		}
	};

	const getAssemblies = async () => {
		try {
			setIsLoading(true);

			const response = await onGetAssembliesList(getCurrentPage());

			setRequests(response.assemblies);
			setTotalRecords(response.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetAssembliesList(getCurrentPage());
			setRequests(response.assemblies);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	const cancelAssemblyHandler = async () => {
		try {
			setIsCanceling(true);
			await onCancelAssembly(selectedRequestId as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.ASSEMBLY_CANCELLED'));
			setCancelModalOpen(false);
			setIsCanceling(false);
			getAssemblies();
			setSelectedRequestId(undefined);
		} catch (error: any) {
			setIsCanceling(false);
			const message = error?.response?.data?.message || t('ACTIONS.ASSEMBLY_NOT_CANCELLED');
			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	const openVoting = async (data: VerificationPayload) => {
		try {
			setIsSubmitting(true);
			const payload = {
				verified_using: data.method,
				[data.method]: data.key,
			};
			await onStartVoting(selectedRequestId as number, payload);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.VOTING_OPENED_SUCCESS'));
			setVotingVerificationModalOpen(false);
			setIsSubmitting(false);
			getAssemblies();
			setSelectedRequestId(undefined);
		} catch (error: any) {
			setIsSubmitting(false);
			const message = error?.response?.data?.message || t('ACTIONS.VOTING_OPENED_FAILED');
			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	const openAttendance = async (data: VerificationPayload) => {
		try {
			setIsSubmitting(true);
			const payload = {
				verified_using: data.method,
				[data.method]: data.key,
			};
			await onStartAttendance(selectedRequestId as number, payload);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.ATTENDANCE_OPENED_SUCCESS'));
			setAttendanceVerificationModalOpen(false);
			setIsSubmitting(false);
			getAssemblies();
			setSelectedRequestId(undefined);
		} catch (error: any) {
			setIsSubmitting(false);
			const message = error?.response?.data?.message || t('ACTIONS.ATTENDANCE_OPENED_FAILED');
			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	return (
		<>
			<ActionListHeaderComponent
				title={t('ACTIONS.ASSEMBLIES')}
				subTitle={t('ACTIONS.ASSEMBLY_DESCRIPTION')}
				costLoading={serviceCostLoading}
				costs={[serviceCost!]}
			/>

			<Table
				columns={columns}
				dataSource={requests}
				scroll={{ x: 600 }}
				rowKey="id"
				loading={isLoading}
				pagination={{
					pageSize: 15,
					total: totalRecords,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreData,
					showTotal: (total) => <TableTotal total={total} />,
				}}
				locale={{ emptyText: <NoData /> }}
			/>

			{/* cancel modal */}
			<ConfirmModalComponent
				open={cancelModalOpen}
				confirmText={t('COMMON.CONFIRM')}
				onConfirm={cancelAssemblyHandler}
				isLoading={isCanceling}
				onDiscard={() => setCancelModalOpen(false)}
				title={t('ACTIONS.CANCEL_ASSEMBLY')}
				note={
					<>
						<p>{t('ACTIONS.CONFIRM_CANCEL_ASSEMBLY')}</p>
						<p className="red fw-medium">{t('ACTIONS.CANT_UNDO')}</p>
					</>
				}
				noteType="red"
			/>

			{/* OPEN VOTING VERIFICATION MODAL */}
			<VerificationModal
				open={votingVerificationModalOpen}
				isLoading={isSubmitting}
				onCancel={() => {
					setVotingVerificationModalOpen(false);
					setSelectedRequestId(undefined);
				}}
				onVerificationSuccess={openVoting}
				module="assembly_openVoting"
			/>

			{/* OPEN ATTENDANCE VERIFICATION MODAL */}
			<VerificationModal
				open={attendanceVerificationModalOpen}
				isLoading={isSubmitting}
				onCancel={() => {
					setAttendanceVerificationModalOpen(false);
					setSelectedRequestId(undefined);
				}}
				onVerificationSuccess={openAttendance}
				module="assembly_openAttendance"
			/>
		</>
	);
};

export default ActionAssembliesListPage;
